import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getPaperMode, getAccentColor } from './themePresets';

const ThemeContext = createContext();

const STORAGE_PAPER = 'theme_paper_mode';
const STORAGE_ACCENT = 'theme_accent_color';
const STORAGE_WIDTH = 'theme_content_width';

export const ThemeProvider = ({ children }) => {
    const [paperMode, setPaperModeState] = useState(() => {
        try {
            const savedPaper = localStorage.getItem(STORAGE_PAPER);
            if (savedPaper && getPaperMode(savedPaper).id === savedPaper) {
                return savedPaper;
            }
            // Backward compatibility with legacy 'theme' key
            const legacyTheme = localStorage.getItem('theme');
            if (legacyTheme === 'dark') return 'dark';
            if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
                return 'dark';
            }
        } catch (e) {}
        return 'light';
    });

    const [accentColor, setAccentColorState] = useState(() => {
        try {
            const savedAccent = localStorage.getItem(STORAGE_ACCENT);
            if (savedAccent && getAccentColor(savedAccent).id === savedAccent) {
                return savedAccent;
            }
        } catch (e) {}
        return 'blue';
    });

    const [contentWidth, setContentWidthState] = useState(() => {
        try {
            const savedWidth = localStorage.getItem(STORAGE_WIDTH);
            if (savedWidth) {
                const parsed = Number(savedWidth);
                if (Number.isFinite(parsed) && parsed >= 50 && parsed <= 100) {
                    return parsed;
                }
            }
        } catch (e) {}
        return 80;
    });

    const isDarkMode = ['dark', 'navy'].includes(paperMode);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_PAPER, paperMode);
            localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
        } catch (e) {}
    }, [paperMode, isDarkMode]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_ACCENT, accentColor);
        } catch (e) {}
    }, [accentColor]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_WIDTH, String(contentWidth));
        } catch (e) {}
    }, [contentWidth]);

    const setPaperMode = useCallback((mode) => {
        setPaperModeState(mode);
    }, []);

    const setAccentColor = useCallback((color) => {
        setAccentColorState(color);
    }, []);

    const setContentWidth = useCallback((width) => {
        setContentWidthState(width);
    }, []);

    const toggleTheme = useCallback(() => {
        setPaperModeState((prev) => (['dark', 'navy'].includes(prev) ? 'light' : 'dark'));
    }, []);

    return (
        <ThemeContext.Provider
            value={{
                paperMode,
                accentColor,
                contentWidth,
                isDarkMode,
                setPaperMode,
                setAccentColor,
                setContentWidth,
                toggleTheme,
            }}
        >
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);
 